import { useQuery } from "react-query";
import { Link as ReactRouterLink, useNavigate } from "react-router-dom";

import { List, ListItem, ListItemButton, Stack, Typography } from "@mui/joy";
import { AddCircleOutline } from "@mui/icons-material";

import { PrimeQueryKeys } from "../shared/constants";
import { ConversationApi } from "../api/conversation";

import { comparatorConversationNewestToOldestUpdate } from "../util/objectUtil";

const styles = {
    newConversationButton: {
        display: "flex",
        alignItems: "center",
        gap: 1,
    },
    divider: {
        width: "100%",
        borderTop: "1px solid var(--joy-palette-primary-500)",
    },
    dividerContainer: { p: 0, minHeight: 0 },
    listContainer: {
        maxWidth: 400,
    },
};

interface ConversationListProps {
    isHeaderDropdown?: boolean;
}

export default function ConversationList({ isHeaderDropdown = false }: ConversationListProps) {
    const navigate = useNavigate();
    const { data: conversationData } = useQuery(
        PrimeQueryKeys.allConversations,
        ConversationApi.getAllConversations,
    );
    const allConversations = (conversationData ?? []).sort(
        comparatorConversationNewestToOldestUpdate,
    );

    const noConversations = allConversations.length === 0;

    return (
        <List sx={isHeaderDropdown ? styles.listContainer : {}}>
            {isHeaderDropdown && (
                <ListItem>
                    <ListItemButton onClick={() => navigate("/")} sx={styles.newConversationButton}>
                        <AddCircleOutline color="primary" fontSize="small" />
                        <Typography noWrap color="primary" fontSize="sm" fontWeight="bold">
                            Ask a question...
                        </Typography>
                    </ListItemButton>
                </ListItem>
            )}

            {isHeaderDropdown && allConversations.length > 0 && (
                <ListItem sx={styles.dividerContainer}>
                    <hr style={styles.divider} />
                </ListItem>
            )}

            {noConversations && !isHeaderDropdown && (
                <Stack spacing={1}>
                    <Typography level="body-sm">
                        You haven't asked any questions yet. Questions you ask will show up here, so
                        you can easily find them later.
                    </Typography>
                </Stack>
            )}

            {allConversations.map((c) => (
                <ListItem key={c.id}>
                    <ListItemButton component={ReactRouterLink} to={`/c/${c.id}`}>
                        <Typography noWrap fontSize="sm">
                            {c.title}
                        </Typography>
                    </ListItemButton>
                </ListItem>
            ))}
        </List>
    );
}
